/**
 * First-login "Quick Start" walkthrough — role aware (patient + clinician/admin).
 *
 * Design:
 *   • Curated short tour by default + optional "Show me everything" expander.
 *   • Patient and clinician get different, route-accurate step sets.
 *   • Persists completion in localStorage per user id — never re-shows unless the
 *     user hits "Restart tour" (profile menu) which dispatches `fl:restart-onboarding`.
 *   • Respects patient entitlements (hides a step only when explicitly denied).
 *   • Respects prefers-reduced-motion via Framer Motion.
 *
 * Non-blocking: floating card, bottom-right; "Skip"/"Finish" close cleanly and it
 * never covers the primary nav or critical controls.
 */
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Dumbbell, Activity, MessageSquare, Video, Watch, FileText,
  BookOpen, MessagesSquare, CreditCard, Calendar, CalendarDays, Users, ClipboardList,
  ClipboardCheck, Inbox, ShieldCheck, ChevronRight, ChevronLeft, X, Rocket,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import useModalA11y from "@/lib/useModalA11y";

const STORAGE_KEY = (uid) => `fl_onboarding_done_${uid}`;

// ── Patient track ───────────────────────────────────────────────────────────
const patientCurated = [
  { to: "/app", icon: LayoutDashboard, title: "Your Today dashboard",
    body: "Your home base — daily quote, quick stats, your top exercises and the check-in slider. If you only open one tab a day, make it this one." },
  { to: "/app/plan", need: "can_access_my_plan", icon: Dumbbell, title: "My Plan & phases",
    body: "The rehab or performance programme your clinician built. Work through each phase in order — sets, reps, rest and tempo are all guided — and tick exercises off as you complete them." },
  { to: "/app", icon: ClipboardCheck, title: "Daily Check-In",
    body: "Each day, log pain, fatigue, sleep and mood from the Today page. It takes seconds and shapes what your clinician sees next." },
  { to: "/app/progress", need: "can_access_metrics", icon: Activity, title: "Progress, Wellness & Tests",
    body: "Switch between Progress, Physical Wellness and Physical Tests to see your trends and results. Your clinician sees the same view — no double-reporting." },
  { to: "/app/messages", icon: MessageSquare, title: "Appointments & Messages",
    body: "Message your Functional LAB team securely, and use Appointments to book, reschedule or cancel visits. Replies arrive as notifications." },
];
const patientExtras = [
  { to: "/app/appointments", icon: Calendar, title: "Appointments", body: "Book, reschedule or cancel visits — in-person or virtual." },
  { to: "/app/videos", icon: Video, title: "Videos", body: "Clips you record show up here for your clinician to review." },
  { to: "/app/forms", need: "can_access_forms", icon: FileText, title: "Forms", body: "Intake and outcome questionnaires — usually one or two per phase." },
  { to: "/app/wearables", need: "can_access_metrics", icon: Watch, title: "Wearables", body: "Connect WHOOP, Garmin or Apple Health so recovery data auto-syncs." },
  { to: "/app/learn", icon: BookOpen, title: "Learn", body: "Short, evidence-based reads matched to your goal." },
  { to: "/app/community", icon: MessagesSquare, title: "Community", body: "Optional space to see how others are progressing." },
  { to: "/app/packages", icon: CreditCard, title: "Membership", body: "Renew, upgrade or pause your plan — cancel anytime." },
  { to: "/security-settings", icon: ShieldCheck, title: "Notifications & settings", body: "Manage notifications, password, security and privacy from your profile menu (top-right) → Security." },
];

// ── Clinician / admin track ───────────────────────────────────────────────────
const clinCurated = [
  { to: "/clinician", icon: Users, title: "Your caseload",
    body: "Start here to see your patients, triage new intakes and open a patient record to review their plan, check-ins and history." },
  { to: "/clinician/calendar", icon: CalendarDays, title: "Calendar & Google sync",
    body: "Manage appointments and availability. Connect your Google Calendar so bookings reserve your time automatically in both directions." },
  { to: "/clinician/library", icon: Dumbbell, title: "Exercise Library approval",
    body: "Create and edit exercises, complete Instructions, Coaching Cues and Safety, then clinically approve and activate them for patient use." },
  { to: "/clinician/templates", icon: ClipboardList, title: "Programme Templates & Builder",
    body: "Build reusable master pathways without code — phases, screening, linked exercises and progressions — then submit for clinical review and activation." },
  { to: "/clinician/assessment", icon: ClipboardCheck, title: "Assessments & follow-up",
    body: "Run assessments and notes, and use the Review Centre and proposals to action follow-ups and personalisation reviews." },
  { to: "/clinician/messages", icon: MessageSquare, title: "Messaging",
    body: "Secure two-way chat with patients on your caseload — text, photos and short videos, all audited." },
];
const clinExtras = [
  { to: "/clinician/review-center", icon: Inbox, title: "Review Centre", body: "Your queue of follow-up actions, proposals and items awaiting sign-off." },
  { to: "/clinician/physical-tests", icon: Activity, title: "Physical Tests", body: "Define and review functional test batteries linked to programme phases." },
  { to: "/clinician/team", icon: Users, title: "Practice & Team", body: "Team coordination, community and practice inquiries." },
];
const adminExtra = { to: "/admin", icon: ShieldCheck, title: "Switch to Admin", body: "As an authorised admin you can open the Admin dashboard from your profile menu (top-right) → Admin Dashboard, or the admin switch on the rail." };

export default function OnboardingTour() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const role = user?.role;
  const isClin = role === "clinician" || role === "admin";
  const isPatient = role === "patient";
  const supported = isPatient || isClin;

  const { curated, extras } = useMemo(() => {
    const ent = user?.entitlements || {};
    const allow = (s) => !s.need || ent[s.need] !== false;
    if (isClin) {
      const ex = [...clinExtras];
      if (user?.is_admin || role === "admin") ex.push(adminExtra);
      return { curated: clinCurated, extras: ex };
    }
    return { curated: patientCurated.filter(allow), extras: patientExtras.filter(allow) };
  }, [isClin, role, user?.is_admin, user?.entitlements]);

  // Part E — the tour may only AUTO-START on the role's home/caseload and must
  // never pop up on (or interrupt) patient profiles, assessments or notes.
  const BLOCKED_RE = /^\/clinician\/(patient|assessment|notes)(\/|$)/;
  const isHomeRoute = isClin ? location.pathname === "/clinician" : location.pathname === "/app";
  const isBlockedRoute = BLOCKED_RE.test(location.pathname);

  useEffect(() => {
    if (!user?.id || !supported) return;
    let done = null;
    try {
      done = typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY(user.id));
    } catch { /* storage disabled — treat as not-done */ }
    if (done) return;
    if (!isHomeRoute || isBlockedRoute) return; // gate auto-start to home/caseload only
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, [user?.id, supported, isHomeRoute, isBlockedRoute]);

  // If the user navigates into a patient profile / assessment / notes while the
  // tour is open, hide it (without marking done) so it never interrupts clinical work.
  useEffect(() => {
    if (isBlockedRoute) setOpen(false);
  }, [isBlockedRoute]);

  const finish = () => {
    try {
      if (user?.id) window.localStorage.setItem(STORAGE_KEY(user.id), new Date().toISOString());
    } catch { /* ignore */ }
    setOpen(false);
  };
  const dialogRef = useModalA11y({ open, onClose: finish });

  // A critical modal can force-close the tour; and the profile menu can restart it.
  useEffect(() => {
    const closeMe = () => setOpen(false);
    const restart = () => {
      try { if (user?.id) window.localStorage.removeItem(STORAGE_KEY(user.id)); } catch { /* ignore */ }
      setShowAll(false); setStep(0); setOpen(true);
    };
    window.addEventListener("fl:close-onboarding", closeMe);
    window.addEventListener("fl:restart-onboarding", restart);
    return () => {
      window.removeEventListener("fl:close-onboarding", closeMe);
      window.removeEventListener("fl:restart-onboarding", restart);
    };
  }, [user?.id]);

  const steps = showAll ? [...curated, ...extras] : curated;
  const current = steps[step];
  const isLast = step === steps.length - 1;
  const Icon = current?.icon || Rocket;
  const next = () => { if (isLast) return finish(); setStep((s) => Math.min(s + 1, steps.length - 1)); };
  const prev = () => setStep((s) => Math.max(s - 1, 0));
  const jumpAndFinish = () => { if (current?.to) nav(current.to); finish(); };

  if (!user?.id || !supported || !current) return null;

  return (
    <AnimatePresence>
      {open && current && (
        <motion.div
          ref={dialogRef}
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[60] bottom-24 md:bottom-6 right-3 md:right-6 w-[92vw] max-w-sm fl-card-glass fl-card-elevated p-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-title-h"
          aria-live="polite"
          aria-label="Quick start tour"
          data-testid="onboarding-tour"
        >
          <button
            onClick={finish}
            className="absolute top-3 right-3 text-zinc-500 hover:text-[color:var(--fl-orange)] fl-tap"
            aria-label="Close tour"
            data-testid="onboarding-close"
          >
            <X size={16} />
          </button>

          <div className="flex items-center gap-1.5 mb-4" data-testid="onboarding-progress">
            {steps.map((_, i) => (
              <span
                key={i}
                className="h-1 rounded-full transition-all duration-300"
                style={{
                  width: i === step ? 24 : 8,
                  background: i <= step ? "var(--fl-orange)" : "color-mix(in oklab, var(--fl-muted-2) 40%, transparent)",
                }}
              />
            ))}
          </div>

          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-10 h-10 rounded-xl grid place-items-center"
              style={{ background: "color-mix(in oklab, var(--fl-orange) 14%, transparent)", border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)" }}
            >
              <Icon size={18} className="text-[color:var(--fl-orange)]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="fl-label text-[color:var(--fl-orange)]">
                {isClin ? "Clinician quick start" : "Quick start"} · Step {step + 1} of {steps.length}
              </div>
              <h3 id="onboarding-title-h" className="fl-heading text-xl mt-1" data-testid="onboarding-title">{current.title}</h3>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--fl-muted)" }} data-testid="onboarding-body">
                {current.body}
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                disabled={step === 0}
                className="fl-btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="onboarding-prev"
              >
                <ChevronLeft size={14} /> Back
              </button>
              {!showAll && step === curated.length - 1 && extras.length > 0 && (
                <button
                  onClick={() => setShowAll(true)}
                  className="text-xs underline decoration-dotted underline-offset-4 hover:text-[color:var(--fl-orange)]"
                  style={{ color: "var(--fl-muted)" }}
                  data-testid="onboarding-showall"
                >
                  Show me everything
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={jumpAndFinish}
                className="text-xs hover:text-[color:var(--fl-orange)]"
                style={{ color: "var(--fl-muted-2)" }}
                data-testid="onboarding-jump"
                title="Open this tab and finish"
              >
                Take me there →
              </button>
              <button onClick={next} className="fl-btn text-xs" data-testid="onboarding-next">
                {isLast ? "Finish" : "Next"} <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
